<script setup lang="ts">
import { ref, onMounted, computed, onBeforeMount } from "vue";
import { Swiper, SwiperSlide } from "swiper/vue";
import "swiper/css";
import "swiper/css/effect-coverflow";
import "swiper/css/pagination";
import { ChevronLeft, ChevronRight } from "lucide-vue-next";
import { EffectCoverflow } from "swiper/modules";
import { useI18n } from "vue-i18n";
import NeuronalBackground from "./NeuronalBackground.vue";
import { useStorePack } from "~/stores/storePack";
import { Scarcity } from "fungi-types";

const storePack = useStorePack();
const handleRevealCard = () => {
  if (cards.value[swipeIndex.value].reveal === 0 && swipeIndex.value > 0) {
    startLayerReveal(swipeIndex.value);
  }
};

const props = withDefaults(
  defineProps<{
    scarcity: string;
  }>(),
  {
    scarcity: "",
  }
);
// Audio setup
const cardRevealSound = new Audio();
cardRevealSound.src =
  "https://storage.googleapis.com/fungiball-assets/pack-opening/card_flipped.mp3";
cardRevealSound.load();

const layerSound = new Audio();
layerSound.src = "https://storage.googleapis.com/fungiball-assets/pack-opening/layers/reveal_3.mp3";
layerSound.load();

// Function to play the card reveal sound
const playCardRevealSound = () => {
  // Reset the audio to the beginning (in case it's already playing)
  cardRevealSound.currentTime = 0;
  // Play the sound
  cardRevealSound.play().catch((error) => {
    console.error("Error playing audio:", error);
  });
};

// Function to play layer reveal sound (can be same or different)
const playLayerRevealSound = () => {
  layerSound.currentTime = 0;
  layerSound.volume = 0.5; // Subtle volume for layer transitions
  layerSound.play().catch((error) => {
    console.error("Error playing layer sound:", error);
  });
};

const defaultBackCard = computed(() => {
  return `https://storage.googleapis.com/fungiball-assets/pack-opening/card_back_${props.scarcity}.png`;
});

const tokenBackCard = computed(() => {
  return `https://storage.googleapis.com/fungiball-assets/pack-opening/card_back_token.png`;
});

const tokenFrontCard = computed(() => {
  return `https://storage.googleapis.com/fungiball-assets/pack-opening/card_front_token.png`;
});

const test = ref(true);
const onSlideChange = (e) => {
  swipeIndex.value = e.realIndex;
  test.value = false;
  handleRevealCard();
};

// With script setup, modules is directly available to the template
const modules = [EffectCoverflow];
const swipeIndex = ref(0);

// Layer reveal animation
const startLayerReveal = (index: number) => {
  // Play initial card flip sound when starting reveal
  playCardRevealSound();
  updateColor();

  // Set initial reveal state to start the animation
  cards.value[index].reveal = 1;

  // Define different delays for first and subsequent layers
  const firstLayerDelay = 1_000; // first layer reveals after 1000ms
  const subsequentLayerDelay = 1_000; // other layers use 2500ms

  const maxLayers = cards.value[index].layers.length;

  // Play sound for final card reveal
  playLayerRevealSound();

  if (index === cards.value.length - 1) {
    showCloseButton.value = true;
  }

  // Schedule reveal of each layer with consistent timing
  for (let layer = 2; layer <= maxLayers; layer++) {
    // Calculate the proper timing - first layer gets firstLayerDelay, others use subsequentLayerDelay
    const delay =
      layer === 2 ? firstLayerDelay : firstLayerDelay + (layer - 2) * subsequentLayerDelay;

    setTimeout(() => {
      // Update the reveal state to show the next layer
      cards.value[index].reveal = layer;

      // When all layers are shown, reveal final card
      if (layer === maxLayers) {
        setTimeout(() => {
          cards.value[index].reveal = maxLayers; // Final card state
        }, subsequentLayerDelay);
      }
    }, delay);
  }
};

const glowColor = computed(() => {
  let scarcity = cards.value[swipeIndex.value]?.scarcity;
  const activeCard = cards.value[swipeIndex.value];
  if (swipeIndex.value === 0 && activeCard.visible) {
    scarcity = props.scarcity as Scarcity;
  }

  if (scarcity === "platinum") return "#9333ea";
  if (scarcity === "gold") return "#eac533";
  if (scarcity === "silver") return "#ebebeb";
  return "#9333ea";
});

const show = ref(false);
const swiperInstance = ref(null);

const cards = ref<
  {
    src: string;
    reveal: number;
    visible: boolean;
    scarcity: Scarcity;
    layers: string[];
  }[]
>([]);

const mapOpenedCards = () => {
  if (storePack.packOpened) {
    let firstCard = {
      src: "https://nft.fungiball.io/skills/thumbs/9391s_600x600.png",
      layers: [],
      reveal: 0,
      visible: false,
      scarcity: storePack.packOpened.type as Scarcity,
    };
    let cardsInpack = storePack.packOpened.cards.map((card) => {
      const layers = 6;
      return {
        src: `https://nft.fungiball.io/skills/thumbs/${card.tokenId}_600x600.png`,
        reveal: 0,
        scarcity: card.scarcity as Scarcity,
        visible: false,
        layers: [...Array(layers)].map((_, i) => {
          return `https://nft.fungiball.io/skills/layers/${card.tokenId}_layer_${i + 1}.png`;
        }),
      };
    });

    let tokenCard = {
      src: tokenFrontCard.value,
      layers: [
        tokenFrontCard.value,
        `https://storage.googleapis.com/fungiball-assets/pack-opening/card_tokens_${storePack.packOpened.tokens}.png`,
      ],
      reveal: 0,
      visible: false,
      scarcity: storePack.packOpened.type as Scarcity,
    };

    cards.value = [firstCard, ...cardsInpack, tokenCard];
  }
};

onMounted(() => {
  // First show the swiper container
  show.value = true;

  // Then animate cards one by one
  animateCardsSequentially();
});

onBeforeMount(() => {
  mapOpenedCards();
});

const animateCardsSequentially = () => {
  const delay = 100; // Delay between each card animation in ms

  cards.value.forEach((card, index) => {
    setTimeout(() => {
      card.visible = true;
      // We're not auto-revealing the cards now, they will remain with back side showing
    }, index * delay);
  });
};

const onSwiperInit = (swiper) => {
  swiperInstance.value = swiper;
};

const indexStart = computed(() => {
  return swipeIndex.value > 1;
});

const indexEnd = computed(() => {
  return swipeIndex.value + 1 === cards.value.length;
});

// Navigation handlers
const handleSwipePrev = () => {
  if (swiperInstance.value && !indexEnd.value) {
    swiperInstance.value.slideNext();
  }
};

const handleSwipeNext = () => {
  if (swiperInstance.value && indexStart.value) {
    swiperInstance.value.slidePrev();
  }
};

const showCloseButton = ref(false);

const emits = defineEmits(["close"]);

const { t } = useI18n();

const handleClose = () => {
  emits("close");
};

// Noise settings
const noiseSettings = ref({
  scale: 8.5, // Initial scale of the noise
  layers: 13, // Number of noise layers
  power: 4.0, // Noise power (higher = more contrast)
  intensity: 1.2, // Noise intensity multiplier
});

const color = computed(() => {
  let scarcity = cards.value[swipeIndex.value].scarcity;
  const activeCard = cards.value[swipeIndex.value];
  if (swipeIndex.value === 0 && activeCard.visible) {
    scarcity = props.scarcity;
  }

  if (scarcity === "platinum") return presetColors[0].values;
  if (scarcity === "gold") return presetColors[1].values;
  if (scarcity === "silver") return presetColors[2].values;
  return presetColors[0].values;
});

// Preset colors
const presetColors = [
  {
    scarcity: "platinum",
    values: { r: 0.5, g: 0.2, b: 0.8 },
    css: "rgb(127, 51, 204)",
  },
  {
    scarcity: "gold",
    values: { r: 0.9, g: 0.8, b: 0.2 },
    css: "rgb(229, 204, 51)",
  },
  {
    scarcity: "silver",
    values: { r: 0.9, g: 0.9, b: 0.9 },
    css: "rgb(229, 229, 229)",
  },
];

const updateColor = () => {
  noiseSettings.value = {
    ...noiseSettings.value,
    power: 1.0,
    intensity: 1.3,
  };

  setTimeout(() => {
    noiseSettings.value = {
      ...noiseSettings.value,
      power: 4.0,
      intensity: 1.3,
    };
  }, 500);
};
</script>

<template>
  <div
    class="fixed inset-0 flex flex-col items-center justify-center z-[9999999999] bg-black/90 gap-10"
  >
    <div class="md:text-4xl md:mt-0 mt-10 text-2xl font-semibold text-center" @click="updateColor">
      <div class="text-base font-normal opacity-50">
        {{ t("_shared.pack", { scarcity: t(`scarcities.${props.scarcity}`) }) }}
      </div>
      <div>{{ t("pack.discover-your-cards") }}</div>
    </div>

    <swiper
      :effect="'coverflow'"
      :grabCursor="true"
      :centeredSlides="true"
      :slidesPerView="'auto'"
      :speed="300"
      :allowTouchMove="true"
      :preventInteractionOnTransition="true"
      :slideToClickedSlide="true"
      :followFinger="true"
      :slidesPerGroup="1"
      :touchReleaseOnEdges="true"
      :momentumRatio="0"
      :momentumBounce="false"
      :momentumBounceRatio="0"
      :touchMoveStopPropagation="true"
      :resistanceRatio="0.3"
      :touchRatio="0.2"
      :longSwipesRatio="0.2"
      :longSwipesMs="200"
      @slideChange="onSlideChange"
      @swiper="onSwiperInit"
      :coverflowEffect="{
        rotate: 25,
        stretch: 20,
        depth: 100,
        modifier: 1,
        slideShadows: false,
      }"
      :modules="modules"
      class="mySwiper transition-all duration-500 ease-in-out"
      :class="show ? 'translate-x-0' : 'translate-x-80'"
    >
      <swiper-slide v-for="(card, index) in cards" :key="index" @click="handleRevealCard">
        <div
          class="card-container"
          :class="{ 'card-visible': card.visible, 'card-hidden': !card.visible }"
        >
          <Transition name="flip">
            <template v-if="card.reveal > 0 && index > 0">
              <div class="layer-container">
                <!-- Show appropriate layer based on reveal state with fade transition -->
                <TransitionGroup name="fade">
                  <img
                    v-if="card.reveal >= 1"
                    :key="card.layers[0]"
                    :src="card.layers[0]"
                    class="card-layer"
                  />
                  <img
                    v-if="card.reveal >= 2"
                    :key="card.layers[1]"
                    :src="card.layers[1]"
                    class="card-layer"
                  />
                  <img
                    v-if="card.reveal >= 3"
                    :key="card.layers[2]"
                    :src="card.layers[2]"
                    class="card-layer"
                  />
                  <img
                    v-if="card.reveal >= 4"
                    :key="card.layers[3]"
                    :src="card.layers[3]"
                    class="card-layer"
                  />
                  <img
                    v-if="card.reveal >= 5"
                    :key="card.layers[4]"
                    :src="card.layers[4]"
                    class="card-layer"
                  />
                  <img
                    v-if="card.reveal >= 6"
                    :key="card.layers[5]"
                    :src="card.layers[5]"
                    class="card-layer last-layer"
                  />
                  <img
                    v-if="card.reveal >= 7"
                    :key="card.layers[6]"
                    :src="card.layers[6]"
                    class="card-layer"
                  />
                </TransitionGroup>
              </div>
            </template>
            <img
              v-else
              :src="index < 5 ? defaultBackCard : tokenBackCard"
              class="card-back"
              :class="index === 0 ? 'opacity-0' : ''"
            />
          </Transition>
          <div
            :style="{ '--glow-color': glowColor }"
            class="glowing-square"
            :class="
              index > 0 && index === swipeIndex && card.reveal < 7 ? 'opacity-100' : 'opacity-0'
            "
          ></div>
        </div>
      </swiper-slide>
    </swiper>
    <div class="flex flex-col items-center">
      <div class="grid-flow-col grid gap-2 items-center">
        <div
          @click="handleSwipeNext"
          class="border border-transparent rounded-full p-1"
          :class="indexStart ? 'opacity-100 hover:border-white cursor-pointer ' : 'opacity-20'"
        >
          <ChevronLeft class="h-6" :strokeWidth="1.5" />
        </div>
        <div class="grid-flow-col grid gap-1 items-center">
          <div
            v-for="(card, index) in cards.slice(1)"
            :key="index"
            class="w-6 h-1 rounded-md transition-colors duration-300 ease-out"
            :class="index + 1 === swipeIndex ? 'bg-neutral-200' : 'bg-neutral-900'"
          ></div>
        </div>
        <div
          @click="handleSwipePrev"
          class="border border-transparent rounded-full p-1"
          :class="indexEnd ? 'opacity-20' : 'hover:border-white cursor-pointer opacity-100'"
        >
          <ChevronRight class="h-6" :strokeWidth="1.5" />
        </div>
      </div>
      <Button
        @click="handleClose"
        type="primary-blue"
        class="mt-4 mb-4 w-full max-w-48 transition-opacity duration-300 ease-out"
        :disabled="!showCloseButton"
        :class="showCloseButton ? 'opacity-100' : 'opacity-0'"
      >
        {{ t("_shared.close") }}
      </Button>
    </div>
    <NeuronalBackground
      class="fixed -z-50"
      :base-color="color"
      :transition-duration="1000"
      :transition-speed="0.08"
      :noise-settings="noiseSettings"
    />
  </div>
</template>

<style scoped>
.card-container {
  perspective: 1000px;
  width: 100%;
  height: 100%;
  transition: all 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.card-hidden {
  opacity: 0;
  transform: translateX(100px) rotateX(90deg);
}

.card-visible {
  opacity: 1;
  transform: translateX(0) rotateX(0);
  animation: bounce-in 1s cubic-bezier(0.36, 0, 0.66, 1.5) forwards;
}

.card-back {
  display: block;
  width: 100%;
  backface-visibility: hidden;
  border-radius: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.layer-container {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 10px;
  overflow: hidden;
}

.card-layer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  border-radius: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

/* Flip animation */
.flip-enter-active,
.flip-leave-active {
  transition: all 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  transform-style: preserve-3d;
  position: absolute;
  top: 0;
  left: 0;
}

.flip-enter-from {
  transform: rotateY(180deg);
  opacity: 0;
}

.flip-leave-to {
  transform: rotateY(-180deg);
  opacity: 0;
}

/* Fade transition for layers */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 3s ease;
}

/* Special longer transition for the last layer */
.fade-enter-active.last-layer,
.fade-leave-active.last-layer {
  transition: opacity 6s ease; /* Twice as long for the last layer */
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Original slide animation - kept as an alternative */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.25s ease-out;
}

.slide-up-enter-from {
  opacity: 0;
  transform: translateY(30px);
}

.slide-up-leave-to {
  opacity: 0;
  transform: translateY(-30px);
}

.swiper {
  width: 100%;
  padding-top: 50px;
  padding-bottom: 50px;
}

.swiper-slide {
  @apply h-[50vh] w-auto aspect-[377/600];
  background-position: center;
  background-size: cover;
}

@keyframes rotate-in {
  0% {
    transform: translateX(100px) rotateX(90deg);
    opacity: 0;
  }
  100% {
    transform: translateX(0) rotateX(0);
    opacity: 1;
  }
}

@keyframes bounce-in {
  0% {
    transform: translateX(100px) rotateX(90deg);
    opacity: 0;
  }
  70% {
    transform: translateX(-10px) rotateX(-10deg);
    opacity: 1;
  }
  85% {
    transform: translateX(5px) rotateX(5deg);
  }
  100% {
    transform: translateX(0) rotateX(0);
  }
}

/* /// */

.container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background-color: #121212; /* Dark background to make the glow effect more visible */
}

.glowing-square {
  @apply !absolute top-0 -z-10  h-[50vh] delay-700 w-auto aspect-[375/600] transition-all  duration-500 ease-in-out;
  position: relative;
  border-radius: 40px;
  background-color: transparent; /* Transparent background */
  border: 2px solid var(--glow-color); /* Purple border */
  box-shadow:
    /* Inner glow */
    0 0 10px var(--glow-color),
    /* Middle glow */ 0 0 15px var(--glow-color),
    /* Outer glow */ 0 0 20px var(--glow-color);

  animation: glow-and-fade 20s forwards;
}

@keyframes pulsate {
  100% {
    box-shadow:
      0 0 5px var(--glow-color),
      0 0 10px var(--glow-color),
      0 0 10px var(--glow-color);
  }
  50% {
    box-shadow:
      0 0 2px var(--glow-color),
      0 0 5px var(--glow-color),
      0 0 15px var(--glow-color);
  }
  0% {
    box-shadow:
      0 0 10px var(--glow-color),
      0 0 15px var(--glow-color),
      0 0 20px var(--glow-color);
  }
}
</style>
